import { Component, Input, Output, EventEmitter, OnInit, DoCheck, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';

@Component({
	selector: 'nx-ahs-visual-builder',
	templateUrl: './visual-builder.component.html',
	styleUrl: './visual-builder.component.scss',
	standalone: false
})
export class VisualBuilderComponent implements OnInit, DoCheck {
	@Input() fields: FormlyFieldConfig[] = [];
	@Input() scriptId: number;
	@Output() fieldsChange = new EventEmitter<FormlyFieldConfig[]>();
	@Output() onFieldsChanged = new EventEmitter<void>();
	@ViewChild('previewFormly') previewFormlyComponent: any;

	selectedField: FormlyFieldConfig | null = null;
	selectedFieldPath: number[] = [];
	previewForm = new FormGroup({});
	previewModel: any = {};
	previewOptions = { formState: {} };
	livePreviewModel: any = {}; // Separate model for the live preview panel
	showPreview = false;
	showValidationErrors = false;

	// Cached model JSON for the preview panel - updated in ngDoCheck to avoid NG0100.
	// Method calls like getHighlightedJson() in template bindings are unstable when the
	// model mutates during child component initialization (e.g. mat-formly-address).
	highlightedModelJson = '';
	private _lastModelSnapshot = '';

	ngDoCheck(): void {
		if (this.showPreview) {
			const json = JSON.stringify(this.previewModel, null, 2);
			if (json !== this._lastModelSnapshot) {
				this._lastModelSnapshot = json;
				this.highlightedModelJson = this.syntaxHighlight(json);
			}
		}
	}

	ngOnInit(): void {
		// Initialize form state with fields for interpolation
		this.updatePreviewOptions();
	}

	private updatePreviewOptions(): void {
		// Make the fields available via options.formState for interpolation
		(this.previewOptions.formState as any).fields = this.fields;
	}

	onPreviewModelChanged(model: any): void {
		this.previewModel = model;
	}

	togglePreview(): void {
		this.showPreview = !this.showPreview;
		if (this.showPreview) {
			// Ensure fresh initialization when opening preview
			this.resetPreview();
		}
	}

	closePreview(): void {
		this.showPreview = false;
	}

	resetPreview(): void {
		this.previewModel = {};
		this.previewForm = new FormGroup({});
		this.previewOptions = { formState: {} };
		this.showValidationErrors = false;
		this.updatePreviewOptions();
	}

	toggleValidationErrors(): void {
		this.showValidationErrors = !this.showValidationErrors;
		if (this.showValidationErrors) {
			this.triggerValidation();
		}
	}

	triggerValidation(): void {
		// Mark all controls as touched to show validation errors
		const form = this.getActualForm();
		if (form) {
			this.markFormGroupTouched(form);
		}
	}

	private getActualForm(): FormGroup | null {
		// Try to get the form from the formly-collector component
		if (this.previewFormlyComponent?.form) {
			return this.previewFormlyComponent.form;
		}
		return this.previewForm;
	}

	private markFormGroupTouched(formGroup: FormGroup): void {
		Object.keys(formGroup.controls).forEach((key) => {
			const control = formGroup.get(key);
			control?.markAsTouched();
			control?.markAsDirty();
			control?.updateValueAndValidity();

			if (control instanceof FormGroup) {
				this.markFormGroupTouched(control);
			}
		});
	}

	get validationErrors(): { field: string; errors: string[] }[] {
		const errors: { field: string; errors: string[] }[] = [];
		const form = this.getActualForm();
		if (form) {
			this.collectValidationErrors(form, '', errors);
		}
		return errors;
	}

	private collectValidationErrors(
		formGroup: FormGroup,
		prefix: string,
		errors: { field: string; errors: string[] }[]
	): void {
		Object.keys(formGroup.controls).forEach((key) => {
			const control = formGroup.get(key);
			const fieldPath = prefix ? `${prefix}.${key}` : key;

			if (control?.errors && (control.touched || this.showValidationErrors)) {
				const fieldErrors = Object.keys(control.errors).map((errorKey) => {
					return this.getErrorMessage(errorKey, control.errors![errorKey]);
				});
				errors.push({ field: fieldPath, errors: fieldErrors });
			}

			if (control instanceof FormGroup) {
				this.collectValidationErrors(control, fieldPath, errors);
			}
		});
	}

	private getErrorMessage(errorKey: string, errorValue: any): string {
		const errorMessages: Record<string, string> = {
			required: 'This field is required',
			email: 'Invalid email address',
			min: `Minimum value is ${errorValue.min}`,
			max: `Maximum value is ${errorValue.max}`,
			minlength: `Minimum length is ${errorValue.requiredLength}`,
			maxlength: `Maximum length is ${errorValue.requiredLength}`,
			pattern: 'Invalid format'
		};
		return errorMessages[errorKey] || `Validation error: ${errorKey}`;
	}

	onFieldSelected(event: { field: FormlyFieldConfig; path: number[] }): void {
		this.selectedField = event.field;
		this.selectedFieldPath = event.path;
	}

	onFieldDropped(event: any): void {
		// Create a new array reference to trigger change detection
		this.fields = [...this.fields];
		this.fieldsChange.emit(this.fields);
		this.onFieldsChanged.emit();
		this.updatePreviewOptions();
	}

	onCanvasFieldsChanged(): void {
		// This is called when fields are reordered, moved, etc.
		// Create a new array reference to trigger change detection
		this.fields = [...this.fields];
		this.fieldsChange.emit(this.fields);
		this.onFieldsChanged.emit();
		this.updatePreviewOptions();
	}

	onFieldUpdatedFromCanvas(event: { field: FormlyFieldConfig; path: number[] }): void {
		// Update from canvas field card
		this.updateFieldAtPath(this.fields, event.path, event.field);
		// Create a new array reference to trigger change detection
		this.fields = [...this.fields];
		this.fieldsChange.emit(this.fields);
		this.onFieldsChanged.emit();
		this.updatePreviewOptions();
	}

	onFieldUpdated(updatedField: FormlyFieldConfig): void {
		if (this.selectedFieldPath.length > 0) {
			this.updateFieldAtPath(this.fields, this.selectedFieldPath, updatedField);
			// Create a new array reference to trigger change detection
			this.fields = [...this.fields];
			this.fieldsChange.emit(this.fields);
			this.onFieldsChanged.emit();
			this.selectedField = updatedField;
			this.updatePreviewOptions();
		}
	}

	onFieldDeleted(): void {
		if (this.selectedFieldPath.length > 0) {
			this.deleteFieldAtPath(this.fields, this.selectedFieldPath);
			// Create a new array reference to trigger change detection
			this.fields = [...this.fields];
			this.fieldsChange.emit(this.fields);
			this.onFieldsChanged.emit();
			this.selectedField = null;
			this.selectedFieldPath = [];
			this.updatePreviewOptions();
		}
	}

	getHighlightedJson(): string {
		const json = JSON.stringify(this.previewModel, null, 2);
		return this.syntaxHighlight(json);
	}

	private syntaxHighlight(json: string): string {
		json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
		return json.replace(
			/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
			(match) => {
				let cls = 'json-number';
				if (/^"/.test(match)) {
					if (/:$/.test(match)) {
						cls = 'json-key';
					} else {
						cls = 'json-string';
					}
				} else if (/true|false/.test(match)) {
					cls = 'json-boolean';
				} else if (/null/.test(match)) {
					cls = 'json-null';
				}
				return '<span class="' + cls + '">' + match + '</span>';
			}
		);
	}

	private updateFieldAtPath(fields: FormlyFieldConfig[], path: number[], updatedField: FormlyFieldConfig): void {
		if (path.length === 1) {
			fields[path[0]] = updatedField;
		} else {
			const [first, ...rest] = path;
			const parent = fields[first];
			if (parent.fieldGroup && Array.isArray(parent.fieldGroup)) {
				this.updateFieldAtPath(parent.fieldGroup, rest, updatedField);
			} else if (
				parent.fieldArray &&
				typeof parent.fieldArray === 'object' &&
				'fieldGroup' in parent.fieldArray
			) {
				const fieldGroup = (parent.fieldArray as any).fieldGroup;
				if (Array.isArray(fieldGroup)) {
					this.updateFieldAtPath(fieldGroup, rest, updatedField);
				}
			}
		}
	}

	private deleteFieldAtPath(fields: FormlyFieldConfig[], path: number[]): void {
		if (path.length === 1) {
			fields.splice(path[0], 1);
		} else {
			const [first, ...rest] = path;
			const parent = fields[first];
			if (parent.fieldGroup && Array.isArray(parent.fieldGroup)) {
				this.deleteFieldAtPath(parent.fieldGroup, rest);
			} else if (
				parent.fieldArray &&
				typeof parent.fieldArray === 'object' &&
				'fieldGroup' in parent.fieldArray
			) {
				const fieldGroup = (parent.fieldArray as any).fieldGroup;
				if (Array.isArray(fieldGroup)) {
					this.deleteFieldAtPath(fieldGroup, rest);
				}
			}
		}
	}
}
